import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import Seo from "@/components/Seo";

import Landing from "@/pages/Landing";
import LoopApp from "@/pages/LoopApp";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Today from "@/pages/Today";
import Story from "@/pages/Story";
import Streak from "@/pages/Streak";
import Recap from "@/pages/Recap";
import Roommate from "@/pages/Roommate";
import Accent from "@/pages/Accent";
import Debate from "@/pages/Debate";
import Quiz from "@/pages/Quiz";
import Vocab from "@/pages/Vocab";
import Review from "@/pages/Review";
import WordExplorer from "@/pages/WordExplorer";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import EducationAdmin from "@/pages/EducationAdmin";
import Education from "@/pages/Education";
import TeacherDashboard from "@/pages/TeacherDashboard";
import ClassPage from "@/pages/ClassPage";
import JoinClass from "@/pages/JoinClass";
import MyClasses from "@/pages/MyClasses";
import MissionLibraryStudio from "@/pages/MissionLibraryStudio";
import StudentClass from "@/pages/StudentClass";
import ResourceStudio from "@/pages/ResourceStudio";
import DiscoveryLab from "@/pages/DiscoveryLab";
import AssetAssignment from "@/pages/AssetAssignment";
import Assignments from "@/pages/Assignments";
import MissionRunner from "@/pages/MissionRunner";
import MissionLibrary from "@/pages/MissionLibrary";
import AiUsage from "@/pages/AiUsage";
import FluentLens from "@/pages/FluentLens";
import TextLens from "@/pages/TextLens";
import ObjectLens from "@/pages/ObjectLens";
import StudyLens from "@/pages/StudyLens";
import SceneLens from "@/pages/SceneLens";
import LensArchive from "@/pages/LensArchive";
import VoiceJournal from "@/pages/VoiceJournal";
import FluentFriend from "@/pages/FluentFriend";
import { Privacy, Terms } from "@/pages/Legal";

function AppRouter() {
  const location = useLocation();

  // Persist referral param from any entry point so it survives until signup.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("fluent_ref", ref);
  }, []);

  // Process OAuth callback synchronously during render (before ProtectedRoute).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<><Seo title="GetFluentLoop · Your life becomes your language course." description="Learn from what you see, think, say, read and experience — GetFluentLoop adapts the language around your real life. Free to start, GDPR-first." />{<Landing />}</>} />
      <Route path="/app" element={<LoopApp />} />
      <Route path="/login" element={<><Seo title="Sign in · FluentLoop" />{<Auth mode="login" />}</>} />
      <Route path="/signup" element={<><Seo title="Get started · FluentLoop" description="Create your free FluentLoop account and start learning a language today." />{<Auth mode="signup" />}</>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/today" element={<ProtectedRoute><Today /></ProtectedRoute>} />
      <Route path="/story" element={<ProtectedRoute><Story /></ProtectedRoute>} />
      <Route path="/streak" element={<ProtectedRoute><Streak /></ProtectedRoute>} />
      <Route path="/recap" element={<ProtectedRoute><Recap /></ProtectedRoute>} />
      <Route path="/roommate" element={<ProtectedRoute><Roommate /></ProtectedRoute>} />
      <Route path="/accent" element={<ProtectedRoute><Accent /></ProtectedRoute>} />
      <Route path="/debate" element={<ProtectedRoute><Debate /></ProtectedRoute>} />
      <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
      <Route path="/vocab" element={<ProtectedRoute><Vocab /></ProtectedRoute>} />
      <Route path="/review" element={<ProtectedRoute><Review /></ProtectedRoute>} />
      <Route path="/vocab/explore" element={<ProtectedRoute><WordExplorer /></ProtectedRoute>} />
      <Route path="/lens" element={<ProtectedRoute><FluentLens /></ProtectedRoute>} />
      <Route path="/lens/text" element={<ProtectedRoute><TextLens /></ProtectedRoute>} />
      <Route path="/lens/object" element={<ProtectedRoute><ObjectLens /></ProtectedRoute>} />
      <Route path="/lens/study" element={<ProtectedRoute><StudyLens /></ProtectedRoute>} />
      <Route path="/lens/scene" element={<ProtectedRoute><SceneLens /></ProtectedRoute>} />
      <Route path="/lens/archive" element={<ProtectedRoute><LensArchive /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><VoiceJournal /></ProtectedRoute>} />
      <Route path="/friend" element={<ProtectedRoute><FluentFriend /></ProtectedRoute>} />
      <Route path="/friend/live" element={<ProtectedRoute><FluentFriend /></ProtectedRoute>} />
      <Route path="/friend/live/:liveId" element={<ProtectedRoute><FluentFriend /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/education" element={<Education />} />
      <Route path="/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/class/:classId" element={<ProtectedRoute><ClassPage /></ProtectedRoute>} />
      <Route path="/join/:token" element={<JoinClass />} />
      <Route path="/join" element={<JoinClass />} />
      <Route path="/my-classes" element={<ProtectedRoute><MyClasses /></ProtectedRoute>} />
      <Route path="/teacher/library" element={<ProtectedRoute><MissionLibraryStudio /></ProtectedRoute>} />
      <Route path="/my-classes/:classId" element={<ProtectedRoute><StudentClass /></ProtectedRoute>} />
      <Route path="/my-classes/:classId/resources/:aid" element={<ProtectedRoute><AssetAssignment /></ProtectedRoute>} />
      <Route path="/education/resources" element={<ProtectedRoute><ResourceStudio /></ProtectedRoute>} />
      <Route path="/discovery" element={<ProtectedRoute><DiscoveryLab /></ProtectedRoute>} />
      <Route path="/assignments" element={<ProtectedRoute><Assignments /></ProtectedRoute>} />
      <Route path="/assignment/:assignmentId" element={<ProtectedRoute><MissionRunner /></ProtectedRoute>} />
      <Route path="/missions" element={<ProtectedRoute><MissionLibrary /></ProtectedRoute>} />
      <Route path="/admin/usage" element={<ProtectedRoute><AiUsage /></ProtectedRoute>} />
      <Route path="/admin/education" element={<ProtectedRoute><EducationAdmin /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <BrowserRouter>
            <AppRouter />
            <CookieBanner />
            <Toaster position="top-center" richColors className="pointer-events-auto" style={{ pointerEvents: "auto" }} toastOptions={{ style: { pointerEvents: "auto" } }} />
          </BrowserRouter>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
